import * as Sentry from "@sentry/browser";

import { ERROR, ERROR_IMPACT, type FailureContext, MAX_DETAIL_LENGTH, TAG } from "./types";

const reportedFailures = new WeakSet<object>();

const isObject = (value: unknown): value is object =>
	(typeof value === "object" && value !== null) || typeof value === "function";

export const isReportedFailure = (error: unknown) => isObject(error) && reportedFailures.has(error);

export const trackFailure = (thrown: unknown, context: FailureContext): void => {
	try {
		const client = Sentry.getClient();
		if (!client || isReportedFailure(thrown)) return;
		const error = isObject(thrown) ? thrown : new Error(String(thrown));
		reportedFailures.add(error);

		const scope = Sentry.getCurrentScope().clone();

		// Separate event: the SDK's already-captured marker would drop a re-capture.
		void Promise.resolve(client.eventFromException(error))
			.then(event => {
				client.captureEvent(
					{
						...event,
						level: context.impact === ERROR_IMPACT.APP_CRASH ? "fatal" : "error",
						fingerprint: context.fingerprint ?? ["{{ default }}", context.impact],
						tags: {
							...event.tags,
							[TAG.LEVEL]: ERROR,
							[TAG.IMPACT]: context.impact,
							[TAG.SOURCE]: context.source,
							...(context.operation ? { [TAG.OPERATION]: context.operation } : {}),
							...(context.runtimeContext ? { [TAG.RUNTIME_CONTEXT]: context.runtimeContext } : {}),
						},
						contexts: {
							...event.contexts,
							...(context.componentStack
								? { react: { componentStack: context.componentStack } }
								: {}),
							...(context.detail
								? { failure: { detail: context.detail.slice(0, MAX_DETAIL_LENGTH) } }
								: {}),
						},
					},
					undefined,
					scope
				);
			})
			.catch(() => undefined);
	} catch {
		// Never block the caller's error fallback.
	}
};

export const runWithFailureReporting = async <T>(
	operation: () => T | PromiseLike<T>,
	context: FailureContext
): Promise<T> => {
	try {
		return await operation();
	} catch (error) {
		trackFailure(error, context);
		throw error;
	}
};
